'use client';

import { c as _c } from "react/compiler-runtime";
import { Helmet as OriginalHelmet, HelmetProvider as OriginalHelmetProvider } from '@dr.pogodin/react-helmet';
import { Children, createContext, useContext, useEffect, useRef } from 'react';

// Shared ref for the last Helmet-set title. A MutationObserver re-applies it
// if Next.js or a router cache restore overwrites document.title.
const TitleOverrideContext = createContext(null);

// Get text content from a React <title> element.
const getTitleText = child => {
  if (!child?.props) return null;
  const {
    children: c
  } = child.props;
  if (typeof c === 'string') return c;
  if (Array.isArray(c)) return c.join('');
  return null;
};

// Provider that watches head > title and re-applies the last Helmet title to
// prevent Next.js or cache restores from overwriting it.
const HelmetProvider = t0 => {
  const $ = _c(8);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const titleRef = useRef("");
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const titleEl = document.querySelector("head > title");
      if (!titleEl) {
        return;
      }
      const observer = new MutationObserver(() => {
        if (titleRef.current && document.title !== titleRef.current) {
          document.title = titleRef.current;
        }
      });
      observer.observe(titleEl, {
        childList: true,
        characterData: true,
        subtree: true
      });
      return () => observer.disconnect();
    };
    t2 = [];
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== children || $[6] !== props) {
    t3 = <TitleOverrideContext.Provider value={titleRef}><OriginalHelmetProvider {...props}>{children}</OriginalHelmetProvider></TitleOverrideContext.Provider>;
    $[5] = children;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

// Helmet wrapper: drop string children and intercept <title> to set
// document.title directly (avoid rendering a competing <title> element).
const Helmet = t0 => {
  const $ = _c(13);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const titleRef = useContext(TitleOverrideContext);
  let t1;
  let titleText;
  if ($[3] !== children) {
    const validChildren = Children.toArray(children).filter(_temp);
    const titleChild = validChildren.find(_temp2);
    titleText = titleChild ? getTitleText(titleChild) : null;
    t1 = validChildren.filter(_temp3);
    $[3] = children;
    $[4] = t1;
    $[5] = titleText;
  } else {
    t1 = $[4];
    titleText = $[5];
  }
  const nonTitleChildren = t1;
  let t2;
  let t3;
  if ($[6] !== titleRef || $[7] !== titleText) {
    t2 = () => {
      if (titleText) {
        if (titleRef) {
          titleRef.current = titleText;
        }
        document.title = titleText;
      }
    };
    t3 = [titleText, titleRef];
    $[6] = titleRef;
    $[7] = titleText;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== nonTitleChildren || $[11] !== props) {
    t4 = <OriginalHelmet {...props}>{nonTitleChildren}</OriginalHelmet>;
    $[10] = nonTitleChildren;
    $[11] = props;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export { Helmet, HelmetProvider };
function _temp(child) {
  return typeof child !== "string";
}
function _temp2(child_0) {
  return child_0?.type === "title";
}
function _temp3(child_1) {
  return child_1?.type !== "title";
}